import { useEffect, useRef } from 'react'
import { SWRConfiguration, useSWRConfig } from 'swr'
import { isVersionError } from 'src/app/AppAuth/rpcAuthorization'
import type { AuthRecord } from 'src/context/authContext'

// A read with obsolete authorization or no enabled subscribers is cancelled,
// not denied by the server. Keep it out of consumer error handlers and retry
// only when an authorized subscriber returns to this cache entry.
export class RpcQueryCancelled extends Error {
  readonly name = 'RpcQueryCancelled'
  constructor() {
    super('Query cancelled because it no longer has an authorized subscriber')
  }
}

export function useQueryErrorHandler(
  config?: SWRConfiguration,
): SWRConfiguration['onError'] {
  const defaults = useSWRConfig()
  return (error, key, configuration) => {
    if (error instanceof RpcQueryCancelled) return
    const onError = config?.onError ?? defaults.onError
    onError?.(error, key, configuration)
  }
}

export function useRecoverCancelledQuery(
  key: string,
  error: unknown,
  enabled: boolean,
  isValidating: boolean,
  isCurrent: () => boolean,
  mutate: () => Promise<unknown>,
): boolean {
  const { cache } = useSWRConfig()
  const cancelled = error instanceof RpcQueryCancelled
  useEffect(() => {
    // SWR may have started revalidation in its layout effect, after this
    // render's snapshot. Read live cache state to avoid a duplicate request.
    const state = cache.get(key)
    if (
      enabled &&
      error instanceof RpcQueryCancelled &&
      !state?.isValidating &&
      isCurrent() &&
      state?.error === error
    ) {
      // This also covers warm entries with automatic revalidation disabled.
      // SWR's no-argument mutate handles fetch errors without rejecting.
      void mutate()
    }
  }, [cache, key, error, enabled, isValidating, isCurrent, mutate])
  return cancelled
}

// A version error makes SWRProvider refresh the token. A same-account refresh
// reloads permissions in the background and changes no query key, so nothing
// else restarts this read (some never retry on their own). Retry it once that
// refresh has settled on a newer token, if this subscriber may still read it.
export function useRecoverAfterTokenRefresh(
  key: string,
  error: unknown,
  enabled: boolean,
  isValidating: boolean,
  auth: AuthRecord,
  mutate: () => Promise<unknown>,
): void {
  const { cache } = useSWRConfig()
  const failedOn = useRef<{ error: unknown; token: string }>()
  useEffect(() => {
    if (!isVersionError(error)) {
      failedOn.current = undefined
      return
    }
    const previous = failedOn.current
    const failed =
      previous && previous.error === error
        ? previous
        : { error, token: auth.accessToken }
    failedOn.current = failed
    // Live cache state, as above: SWR may already be revalidating this entry
    // (an account switch flips its key), and the error must still be current.
    const state = cache.get(key)
    if (
      enabled &&
      !state?.isValidating &&
      state?.error === error &&
      !auth.isAuthLoading &&
      !auth.permissionsStale &&
      auth.accessToken !== failed.token
    ) {
      failedOn.current = undefined
      void mutate()
    }
  }, [
    cache,
    key,
    error,
    enabled,
    isValidating,
    auth.accessToken,
    auth.isAuthLoading,
    auth.permissionsStale,
    mutate,
  ])
}
